import { FunctionComponent, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  MenuItem,
  rucksack,
  Select,
  SelectChangeEvent,
  Typography,
} from '@black-bear-energy/rucksack'
import {
  FormControl,
  InputLabel,
  ListItemText,
  OutlinedInput,
} from '@mui/material'
import { CheckRounded, CloseRounded } from '@mui/icons-material'
import { CenteredModal } from '../CenteredModal'
import { BoldText } from './BoldText'
import { FileUploadInput } from './FileUploadInput'
import { useUploadChecklistDocument } from '../../api/hooks/useUploadChecklistDocument'

export const ChecklistUploadModal: FunctionComponent<
  ChecklistUploadModalProps
> = ({ open, checklistName, documents, onClose }) => {
  const [selectedFile, setSelectedFile] = useState<File>()
  const [documentsUploaded, setDocumentsUploaded] = useState<number[]>([])
  const { mutate: uploadChecklistDocument } = useUploadChecklistDocument()

  const documentIdToName: Map<number, string> = new Map<number, string>(
    documents.map((doc) => [doc.id, doc.name])
  )

  const handleChange = (event: SelectChangeEvent) => {
    const value = event.target.value
    setDocumentsUploaded(
      // On autofill we get a stringified value.
      typeof value === 'string'
        ? value.split(',').map(parseInt)
        : (value as number[])
    )
  }

  /** When the user clicks 'Save', upload the file to every selected document and close the modal. */
  const handleSave = () => {
    if (!selectedFile) {
      return
    }
    for (const documentId of documentsUploaded) {
      const documentName = documentIdToName.get(documentId)
      if (!documentName) {
        throw new Error(
          `Cound not find name for document with id ${documentId}`
        )
      }
      uploadChecklistDocument({
        documentId,
        documentName,
        files: [selectedFile],
      })
    }
    handleClose()
  }

  const handleClose = () => {
    // clear out the selected files and documents, in case the user opens this modal again
    setSelectedFile(undefined)
    setDocumentsUploaded([])
    onClose()
  }

  const saveDisabled = selectedFile == null || documentsUploaded.length == 0

  return (
    <CenteredModal open={open}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          alignSelf: 'stretch',
        }}
      >
        <Typography variant="h6">Checklist Upload</Typography>
        <IconButton onClick={handleClose}>
          <CloseRounded />
        </IconButton>
      </Box>
      <Typography variant="body1">
        Upload a file for the <BoldText>{checklistName}</BoldText> checklist.
      </Typography>
      <FileUploadInput
        onSelectedFileChange={(files: File[]) => setSelectedFile(files[0])}
        singleFileSelect={true}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '8px',
          alignSelf: 'stretch',
        }}
      >
        <Typography variant="body2">
          Select which Document(s) have been uploaded
        </Typography>
        <FormControl sx={{ width: '100%' }} size="small">
          <InputLabel
            id="checklist-upload-modal-document-label"
            htmlFor="checklist-upload-modal-document"
            sx={{
              '&.Mui-focused': {
                color: rucksack.palette.primary.main,
              },
            }}
          >
            Document(s)
          </InputLabel>
          <Select
            labelId="checklist-upload-modal-document-label"
            multiple
            value={documentsUploaded}
            onChange={handleChange}
            input={
              <OutlinedInput
                label="Document(s)"
                id="checklist-upload-modal-document"
                sx={{
                  borderRadius: rucksack.shape.borderRadius,
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: rucksack.palette.primary.main,
                  },
                }}
              />
            }
            autoWidth
            renderValue={(selected) => {
              const array = selected as number[]
              const names = array.map((id) => documentIdToName.get(id))
              return names.join(', ')
            }}
            MenuProps={MenuProps}
          >
            {documents.map((doc) => (
              <MenuItem value={doc.id} key={doc.id}>
                <Checkbox checked={documentsUploaded.includes(doc.id)} />
                <ListItemText primary={doc.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: '14px',
          alignSelf: 'stretch',
        }}
      >
        <Button
          sx={{
            color: rucksack.palette.primary.dark,
            border: '1px solid rgba(73, 173, 214, 0.50)',
            padding: '6px 16px',
          }}
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saveDisabled}
          startIcon={<CheckRounded />}
          sx={{
            background: saveDisabled
              ? rucksack.palette.action.disabledBackground
              : rucksack.palette.primary.light,
            color: saveDisabled
              ? rucksack.palette.action.disabled
              : rucksack.palette.primary.contrastText,
            padding: '6px 16px',
          }}
        >
          Save
        </Button>
      </Box>
    </CenteredModal>
  )
}

export interface ChecklistUploadModalProps {
  open: boolean
  checklistName: string
  documents: { id: number; name: string }[]
  onClose: () => void
}

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 568,
    },
  },
}
